import * as React from 'react'

export type StatusVariant = 'success' | 'error' | 'pending'

type NotificationProps = {
  variant: StatusVariant
  open?: boolean
  message: string
  onClose: () => void
}
type DynamicNotificationProps = Omit<NotificationProps, 'onClose'>

const GlobalNotificationContext = React.createContext<{
  notify: (notifProps: DynamicNotificationProps) => undefined
  notificationProps: NotificationProps
} | null>(null)

export const useNotify = () => {
  const notifyContext = React.useContext(GlobalNotificationContext)
  if (!notifyContext) throw new Error('useNotify called outside of GlobalNotificationContext')
  return notifyContext
}

type GlobalNotificationProviderProps = {
  children: React.ReactNode
}
export const GlobalNotificationProvider = ({ children }: GlobalNotificationProviderProps) => {
  const [notificationProps, setNotificationProps] = React.useState<DynamicNotificationProps>({
    open: false,
    variant: 'success',
    message: '',
  })
  const notify = React.useCallback((notifProps: DynamicNotificationProps) => {
    setNotificationProps(notifProps)
    return undefined // required so that notifications can be used in mutation callbacks
  }, [])
  const onClose = React.useCallback(
    () => setNotificationProps({ ...notificationProps, open: false }),
    [notificationProps, setNotificationProps]
  )

  return (
    <GlobalNotificationContext.Provider
      value={{ notify, notificationProps: { ...notificationProps, onClose } }}
    >
      {children}
    </GlobalNotificationContext.Provider>
  )
}
